import { useCallback, useRef } from 'react';
import type { ReferenceLibraryMeta, ReferenceSharedMetadata } from '@scoutaround/script-breakdown-shared';

import { api } from '../../lib/api.js';
import {
  useUploadQueue,
  type QueuedUpload,
  type UploadControl,
} from '../upload/useUploadQueue.js';
import { isVideoFile } from './format.js';
import { grabPosterFrame } from './poster.js';

export {
  UPLOAD_CONCURRENCY,
  type RejectedFile,
  type UploadState,
  type UploadSummary,
} from '../upload/useUploadQueue.js';

/** A library upload: one file and the shared fields it is sent with (fixed at its first attempt). */
export type UploadItem = QueuedUpload<ReferenceSharedMetadata>;

/**
 * THE LIBRARY'S BULK UPLOAD — the shared engine (`components/upload/useUploadQueue.ts`:
 * three at a time, byte progress, keep what worked, Retry under the original key)
 * with the library's own send: `uploadReference` with the shared groups, tags, links
 * and category, and a poster frame the browser takes from a video.
 *
 * Files wait for `start(metadata)`, because the drawer's shared fields are chosen
 * after the files are.
 */
export function useBulkUpload(
  projectId: string,
  upload: ReferenceLibraryMeta['upload'] | undefined,
  onUploaded: () => void,
) {
  const posters = useRef(new Map<string, Blob | null>());
  const limitRef = useRef(upload);
  limitRef.current = upload;

  const send = useCallback(
    async (item: UploadItem, control: UploadControl) => {
      let poster: Blob | null = null;
      if (isVideoFile(item.file)) {
        if (!posters.current.has(item.key)) posters.current.set(item.key, await grabPosterFrame(item.file));
        poster = posters.current.get(item.key) ?? null;
        // The poster travels in the same request as the file, and nginx caps the whole
        // body at the upload limit: a file close to the limit goes without one (its
        // card shows the video icon) rather than being refused.
        const max = limitRef.current?.maxBytes;
        if (poster !== null && max !== undefined && item.file.size + poster.size > max - 256 * 1024) {
          poster = null;
        }
      }
      return api.uploadReference(
        projectId,
        item.file,
        { ...(item.metadata ?? { groupIds: [], tags: [], links: [] }), clientRequestId: item.clientRequestId },
        { poster, signal: control.signal, onProgress: control.onProgress },
      );
    },
    [projectId],
  );

  const queue = useUploadQueue<ReferenceSharedMetadata>(upload, send, onUploaded);
  const { reset: resetQueue } = queue;

  /** Clears the finished list once nothing is sending. */
  const reset = useCallback(() => {
    if (resetQueue()) posters.current.clear();
  }, [resetQueue]);

  return {
    items: queue.items,
    rejected: queue.rejected,
    summary: queue.summary,
    started: queue.started,
    add: queue.add,
    start: queue.start,
    retryFailed: queue.retryFailed,
    remove: queue.remove,
    reset,
  };
}

export type BulkUpload = ReturnType<typeof useBulkUpload>;
